import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import { Container, OfferSupportAlert, Box } from '@occc-app/design-system';
import { LoadingOverlay } from '@occc/ui-design-system';
import { OfferFragment } from '@occc/graphql';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { getAwardCatalogUpdatePageHeaderLinks } from '../utils';
import { AwardCatalogFormWrapper } from '../components/AwardCatalogFormWrapper';
import { CONFIGURE_PAGE_STATE, AWARD_TYPES } from '../types';
import { getAwardSubtype } from '../AwardDetailsPage/utils';

type AwardCatalogUpdatePageProps = {
  formsMap: Map<CONFIGURE_PAGE_STATE, (newAward: OfferFragment) => JSX.Element>;
  award?: OfferFragment;
  loading: boolean;
  pageState: CONFIGURE_PAGE_STATE;
  awardType: AWARD_TYPES;
  setPageState: (value: CONFIGURE_PAGE_STATE) => void;
};

export const AwardCatalogUpdatePage = ({
  formsMap,
  award,
  loading,
  pageState,
  awardType,
  setPageState,
}: AwardCatalogUpdatePageProps) => {
  const isUserPermittedToEditAward = useIsUserPermittedTo(Feature.AWARD_EDIT);

  const awardCatalogPageLinkLabel = isUserPermittedToEditAward
    ? 'Configure Award'
    : 'Award Configurations';

  const links = getAwardCatalogUpdatePageHeaderLinks(
    award ? award.name : 'Loading',
    award?.id || 'Loading',
    awardCatalogPageLinkLabel,
  );

  const isAwardSupported = useMemo(() => {
    if (!award) {
      return true;
    }
    return !!getAwardSubtype(award);
  }, [award]);

  const getForm = formsMap.get(pageState);

  const renderPageContent = () => {
    if (isAwardSupported) {
      let awardCatalogTitle = 'Award Catalog Configurations';

      if (isUserPermittedToEditAward) {
        awardCatalogTitle = 'Edit Award';
      }

      return (
        <CanEditFormFields isEditable={isUserPermittedToEditAward}>
          <AwardCatalogFormWrapper
            title={awardCatalogTitle}
            links={links}
            awardType={awardType}
            pageState={pageState}
            setPageState={setPageState}
          >
            {/* position relative is used to position loading overlay (absolute) inside the box */}
            <Box position='relative'>
              {loading && <LoadingOverlay rounded />}
              {award && getForm && getForm(award)}
            </Box>
          </AwardCatalogFormWrapper>
        </CanEditFormFields>
      );
    }

    return (
      <Container maxWidth='1024px'>
        <OfferSupportAlert
          title='Command Center does not yet support this configuration.'
          fallbackLink={`/offers/${award?.id}`}
          fallbackLinkTitle='View the original Award'
        />
      </Container>
    );
  };

  return (
    <>
      <Helmet>
        <title>{awardCatalogPageLinkLabel}</title>
      </Helmet>
      {renderPageContent()}
    </>
  );
};
